import { motion, useScroll, useTransform } from "framer-motion";
import { useRef } from "react";
import { Button } from "@/components/ui/button";
import { ArrowRight } from "lucide-react";
import { useLanguage, translations } from "@/i18n/LanguageContext";

interface HeroProps {
  onHealthCheck: () => void;
}

const Hero = ({ onHealthCheck }: HeroProps) => {
  const { t } = useLanguage();
  const ref = useRef<HTMLElement>(null);
  const { scrollYProgress } = useScroll({
    target: ref,
    offset: ["start start", "end start"],
  });

  const textY = useTransform(scrollYProgress, [0, 1], ["0%", "30%"]);
  const opacity = useTransform(scrollYProgress, [0, 0.8], [1, 0]);
  const scale = useTransform(scrollYProgress, [0, 0.8], [1, 0.95]);

  return (
    <section ref={ref} className="relative min-h-screen flex items-center justify-center overflow-hidden">
      {/* Animated accent lines */}
      <motion.div
        initial={{ height: 0 }}
        animate={{ height: 160 }}
        transition={{ duration: 1.5, delay: 0.5, ease: "easeOut" }}
        className="absolute top-1/2 left-0 w-px -translate-y-1/2 bg-gradient-to-b from-transparent via-primary/15 to-transparent"
      />
      <motion.div
        initial={{ height: 0 }}
        animate={{ height: 160 }}
        transition={{ duration: 1.5, delay: 0.7, ease: "easeOut" }}
        className="absolute top-1/2 right-0 w-px -translate-y-1/2 bg-gradient-to-b from-transparent via-primary/15 to-transparent"
      />

      <motion.div style={{ y: textY, opacity, scale }} className="container relative mx-auto px-6 pt-40 pb-24">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 1, ease: "easeOut" }}
          className="max-w-3xl mx-auto text-center"
        >
          <motion.span
            initial={{ opacity: 0, letterSpacing: "0.1em" }}
            animate={{ opacity: 1, letterSpacing: "0.3em" }}
            transition={{ duration: 1.4, ease: "easeOut", delay: 0.2 }}
            className="text-xs text-primary tracking-[0.3em] uppercase mb-8 block"
          >
            {t(translations.hero.tag)}
          </motion.span>

          <h1
            className="text-5xl sm:text-6xl md:text-8xl tracking-tight leading-[1.05] mb-8"
          >
            {t(translations.hero.h1Pre)}{" "}
            <span className="text-primary italic">{t(translations.hero.h1Accent)}</span>{" "}
            {t(translations.hero.h1Post)}
          </h1>

          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.5 }}
            className="text-base text-muted-foreground max-w-xl mx-auto mb-14 leading-relaxed"
          >
            {t(translations.hero.description)}
          </motion.p>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.7 }}
            className="flex flex-col sm:flex-row gap-4 justify-center"
          >
            <Button variant="navy" size="lg" onClick={onHealthCheck} className="text-xs tracking-widest uppercase px-10">
              {t(translations.hero.cta1)}
              <ArrowRight className="h-3.5 w-3.5 ml-2" strokeWidth={1.5} />
            </Button>
            <Button variant="navy-outline" size="lg" asChild className="text-xs tracking-widest uppercase px-10">
              <a href="#services">{t(translations.hero.cta2)}</a>
            </Button>
          </motion.div>
        </motion.div>

        {/* Stats bar */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 1, delay: 0.9 }}
          className="mt-28 max-w-2xl mx-auto grid grid-cols-3 gap-8"
        >
          {[
            { value: t(translations.hero.stat1Value), label: t(translations.hero.stat1Label) },
            { value: t(translations.hero.stat2Value), label: t(translations.hero.stat2Label) },
            { value: t(translations.hero.stat3Value), label: t(translations.hero.stat3Label) },
          ].map((stat, i) => (
            <motion.div
              key={i}
              initial={{ opacity: 0, scale: 0.8 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.6, delay: 1 + i * 0.15 }}
              className="text-center"
            >
              <div className="text-2xl sm:text-3xl font-semibold text-primary">{stat.value}</div>
              <div className="text-[10px] text-muted-foreground mt-2 tracking-widest uppercase">{stat.label}</div>
            </motion.div>
          ))}
        </motion.div>
      </motion.div>
    </section>
  );
};

export default Hero;
